import { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import WebScrollView from '../../components/WebScrollView';
import { supabase } from '../../lib/supabase';
import { shadows } from '../../lib/styles';
import {
  Copy,
  KeyRound,
  Plus,
  Search,
  Trash2,
  X,
} from '../../lib/icons';

interface ApiKeyRow {
  id: string;
  user_id: string;
  name: string;
  key_prefix: string;
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  is_active: boolean;
  user_full_name?: string | null;
}

interface ProfileOption {
  id: string;
  full_name: string | null;
}

interface GeneratedKey {
  key: string;
  id: string;
  name: string;
  prefix: string;
  user_id: string;
  user_full_name: string | null;
  expires_at: string | null;
}

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const copyToClipboard = async (value: string): Promise<boolean> => {
  try {
    if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch (err) {
    console.error('Clipboard write failed:', err);
  }
  return false;
};

const ApiKeysTab = () => {
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [profilesById, setProfilesById] = useState<Record<string, ProfileOption>>({});
  const [profileOptions, setProfileOptions] = useState<ProfileOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyUserId, setNewKeyUserId] = useState('');
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiresAt, setNewKeyExpiresAt] = useState('');
  const [creating, setCreating] = useState(false);
  const [generated, setGenerated] = useState<GeneratedKey | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const [userPickerQuery, setUserPickerQuery] = useState('');
  const [revokingId, setRevokingId] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);

      const [{ data: keyRows, error: keysError }, { data: profileRows, error: profilesError }] =
        await Promise.all([
          supabase
            .from('api_keys')
            .select('*')
            .order('created_at', { ascending: false }),
          supabase
            .from('profiles')
            .select('id, full_name')
            .order('full_name', { ascending: true }),
        ]);

      if (keysError) throw keysError;
      if (profilesError) throw profilesError;

      const profileMap: Record<string, ProfileOption> = {};
      (profileRows || []).forEach((p: ProfileOption) => {
        profileMap[p.id] = p;
      });
      setProfilesById(profileMap);
      setProfileOptions(profileRows || []);

      const enriched = (keyRows || []).map((row: ApiKeyRow) => ({
        ...row,
        user_full_name: profileMap[row.user_id]?.full_name ?? null,
      }));
      setKeys(enriched);
    } catch (err) {
      console.error('Failed to load API keys:', err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const filteredKeys = useMemo(() => {
    if (!searchQuery.trim()) return keys;
    const q = searchQuery.trim().toLowerCase();
    return keys.filter((k) => {
      const name = k.name?.toLowerCase() ?? '';
      const fullName = k.user_full_name?.toLowerCase() ?? '';
      const prefix = k.key_prefix.toLowerCase();
      return name.includes(q) || fullName.includes(q) || prefix.includes(q);
    });
  }, [keys, searchQuery]);

  const filteredProfileOptions = useMemo(() => {
    if (!userPickerQuery.trim()) return profileOptions.slice(0, 50);
    const q = userPickerQuery.trim().toLowerCase();
    return profileOptions
      .filter((p) => (p.full_name || '').toLowerCase().includes(q) || p.id.toLowerCase().includes(q))
      .slice(0, 50);
  }, [profileOptions, userPickerQuery]);

  const openCreateModal = () => {
    setNewKeyUserId('');
    setNewKeyName('');
    setNewKeyExpiresAt('');
    setUserPickerQuery('');
    setGenerated(null);
    setKeyCopied(false);
    setError(null);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setGenerated(null);
    setKeyCopied(false);
  };

  const handleGenerate = async () => {
    if (!newKeyUserId) {
      setError('Please pick a user');
      return;
    }
    if (!newKeyName.trim()) {
      setError('Please enter a name for the key');
      return;
    }

    try {
      setCreating(true);
      setError(null);

      const expires_at = newKeyExpiresAt ? new Date(newKeyExpiresAt).toISOString() : null;
      if (expires_at && Number.isNaN(new Date(expires_at).getTime())) {
        throw new Error('Invalid expiration date');
      }

      const { data, error: fnError } = await supabase.functions.invoke('generate-api-key', {
        body: {
          user_id: newKeyUserId,
          name: newKeyName.trim(),
          expires_at,
        },
      });

      if (fnError) throw fnError;
      if (!data || typeof data !== 'object' || !data.key) {
        throw new Error((data && (data as { error?: string }).error) || 'No key returned');
      }

      const profile = profilesById[newKeyUserId];
      const result: GeneratedKey = {
        key: data.key as string,
        id: data.id as string,
        name: data.name as string,
        prefix: data.prefix as string,
        user_id: data.user_id as string,
        user_full_name: profile?.full_name ?? null,
        expires_at: (data.expires_at as string | null) ?? null,
      };
      setGenerated(result);
      setSuccess('API key generated. Copy it now — it will not be shown again.');
      setTimeout(() => setSuccess(null), 5000);
      await load();
    } catch (err) {
      console.error('Generate API key failed:', err);
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const handleCopyKey = async () => {
    if (!generated) return;
    const ok = await copyToClipboard(generated.key);
    setKeyCopied(ok);
    if (ok) {
      setTimeout(() => setKeyCopied(false), 2500);
    }
  };

  const handleRevoke = async (row: ApiKeyRow) => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const ok = window.confirm(
        `Revoke API key "${row.name}"? This cannot be undone and the key will stop working immediately.`,
      );
      if (!ok) return;
    }
    try {
      setRevokingId(row.id);
      setError(null);
      const { error: updateError } = await supabase
        .from('api_keys')
        .update({
          is_active: false,
          revoked_at: new Date().toISOString(),
        })
        .eq('id', row.id);
      if (updateError) throw updateError;
      setSuccess(`Revoked "${row.name}"`);
      setTimeout(() => setSuccess(null), 3000);
      await load();
    } catch (err) {
      console.error('Revoke failed:', err);
      setError((err as Error).message);
    } finally {
      setRevokingId(null);
    }
  };

  const renderRowStatus = (row: ApiKeyRow) => {
    if (!row.is_active || row.revoked_at) {
      return (
        <View style={[styles.statusBadge, styles.statusBadgeRevoked]}>
          <Text style={styles.statusBadgeText}>Revoked</Text>
        </View>
      );
    }
    if (row.expires_at && new Date(row.expires_at) <= new Date()) {
      return (
        <View style={[styles.statusBadge, styles.statusBadgeExpired]}>
          <Text style={styles.statusBadgeText}>Expired</Text>
        </View>
      );
    }
    return (
      <View style={[styles.statusBadge, styles.statusBadgeActive]}>
        <Text style={styles.statusBadgeText}>Active</Text>
      </View>
    );
  };

  return (
    <View style={styles.root}>
      <View style={styles.card}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <KeyRound size={22} color="#2563eb" />
            <Text style={styles.title}>API Keys</Text>
          </View>
          <Pressable style={styles.primaryButton} onPress={openCreateModal}>
            <Plus size={16} color="#ffffff" />
            <Text style={styles.primaryButtonText}>Generate Key</Text>
          </Pressable>
        </View>

        <Text style={styles.subtitle}>
          API keys are used by the Honeydone CLI and AI agents. Each key is scoped to a single
          user; system manager users get cross-account access. Keys are shown to you exactly once
          when generated.
        </Text>

        <View style={styles.searchContainer}>
          <Search size={18} color="#6b7280" />
          <TextInput
            style={styles.searchInput}
            placeholder="Search by user name, key name, or prefix"
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor="#9ca3af"
          />
          {searchQuery.length > 0 && (
            <Pressable onPress={() => setSearchQuery('')} style={styles.clearSearchButton}>
              <X size={16} color="#6b7280" />
            </Pressable>
          )}
        </View>

        {error && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}
        {success && (
          <View style={styles.successContainer}>
            <Text style={styles.successText}>{success}</Text>
          </View>
        )}

        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#9ca3af" />
          </View>
        ) : filteredKeys.length === 0 ? (
          <View style={styles.emptyContainer}>
            <KeyRound size={40} color="#9ca3af" />
            <Text style={styles.emptyTitle}>No API keys</Text>
            <Text style={styles.emptyText}>
              Generate a key to allow the Honeydone CLI or an AI agent to read and modify data on
              behalf of a user.
            </Text>
          </View>
        ) : (
          <WebScrollView style={styles.listScroll}>
            <View style={styles.list}>
              {filteredKeys.map((row) => (
                <View key={row.id} style={styles.keyCard}>
                  <View style={styles.keyCardHeader}>
                    <View style={styles.keyCardHeaderLeft}>
                      <Text style={styles.keyName}>{row.name}</Text>
                      {renderRowStatus(row)}
                    </View>
                    <Pressable
                      style={[
                        styles.iconButton,
                        (revokingId === row.id || !row.is_active || !!row.revoked_at) &&
                          styles.iconButtonDisabled,
                      ]}
                      onPress={() => handleRevoke(row)}
                      disabled={
                        revokingId === row.id || !row.is_active || !!row.revoked_at
                      }
                    >
                      {revokingId === row.id ? (
                        <ActivityIndicator size="small" color="#dc2626" />
                      ) : (
                        <Trash2 size={16} color="#dc2626" />
                      )}
                    </Pressable>
                  </View>

                  <View style={styles.keyCardRow}>
                    <Text style={styles.metaLabel}>User</Text>
                    <Text style={styles.metaValue}>{row.user_full_name || row.user_id}</Text>
                  </View>
                  <View style={styles.keyCardRow}>
                    <Text style={styles.metaLabel}>Prefix</Text>
                    <Text style={styles.monoValue}>{row.key_prefix}…</Text>
                  </View>
                  <View style={styles.keyCardRow}>
                    <Text style={styles.metaLabel}>Created</Text>
                    <Text style={styles.metaValue}>{formatDate(row.created_at)}</Text>
                  </View>
                  <View style={styles.keyCardRow}>
                    <Text style={styles.metaLabel}>Last used</Text>
                    <Text style={styles.metaValue}>{formatDate(row.last_used_at)}</Text>
                  </View>
                  <View style={styles.keyCardRow}>
                    <Text style={styles.metaLabel}>Expires</Text>
                    <Text style={styles.metaValue}>{formatDate(row.expires_at)}</Text>
                  </View>
                </View>
              ))}
            </View>
          </WebScrollView>
        )}
      </View>

      <Modal
        visible={showCreateModal}
        transparent
        animationType="fade"
        onRequestClose={closeCreateModal}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {generated ? 'API Key Generated' : 'Generate API Key'}
              </Text>
              <Pressable onPress={closeCreateModal} style={styles.modalCloseBtn}>
                <X size={20} color="#6b7280" />
              </Pressable>
            </View>

            {generated ? (
              <View style={styles.generatedContainer}>
                <Text style={styles.generatedHeading}>
                  Copy this key now. It will not be shown again.
                </Text>
                <View style={styles.generatedKeyBox}>
                  <Text style={styles.generatedKeyText} selectable>
                    {generated.key}
                  </Text>
                </View>
                <Pressable style={styles.copyButton} onPress={handleCopyKey}>
                  <Copy size={16} color="#ffffff" />
                  <Text style={styles.copyButtonText}>
                    {keyCopied ? 'Copied!' : 'Copy to clipboard'}
                  </Text>
                </Pressable>

                <View style={styles.generatedMeta}>
                  <View style={styles.keyCardRow}>
                    <Text style={styles.metaLabel}>User</Text>
                    <Text style={styles.metaValue}>
                      {generated.user_full_name || generated.user_id}
                    </Text>
                  </View>
                  <View style={styles.keyCardRow}>
                    <Text style={styles.metaLabel}>Name</Text>
                    <Text style={styles.metaValue}>{generated.name}</Text>
                  </View>
                  {generated.expires_at && (
                    <View style={styles.keyCardRow}>
                      <Text style={styles.metaLabel}>Expires</Text>
                      <Text style={styles.metaValue}>{formatDate(generated.expires_at)}</Text>
                    </View>
                  )}
                </View>

                <Pressable style={styles.primaryButton} onPress={closeCreateModal}>
                  <Text style={styles.primaryButtonText}>Done</Text>
                </Pressable>
              </View>
            ) : (
              <View style={styles.formContainer}>
                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>User</Text>
                  <TextInput
                    style={styles.formInput}
                    placeholder="Search users by name or ID"
                    value={userPickerQuery}
                    onChangeText={setUserPickerQuery}
                    placeholderTextColor="#9ca3af"
                  />
                  <View style={styles.userPickerList}>
                    <WebScrollView style={styles.userPickerScroll}>
                      {filteredProfileOptions.length === 0 ? (
                        <Text style={styles.userPickerEmpty}>No matching users</Text>
                      ) : (
                        filteredProfileOptions.map((p) => {
                          const selected = newKeyUserId === p.id;
                          return (
                            <Pressable
                              key={p.id}
                              style={[
                                styles.userPickerItem,
                                selected && styles.userPickerItemSelected,
                              ]}
                              onPress={() => setNewKeyUserId(p.id)}
                            >
                              <Text
                                style={[
                                  styles.userPickerItemText,
                                  selected && styles.userPickerItemTextSelected,
                                ]}
                              >
                                {p.full_name || '(no name)'}
                              </Text>
                              <Text style={styles.userPickerItemSub}>{p.id}</Text>
                            </Pressable>
                          );
                        })
                      )}
                    </WebScrollView>
                  </View>
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>Name</Text>
                  <TextInput
                    style={styles.formInput}
                    placeholder="e.g. Bryce's CLI on laptop"
                    value={newKeyName}
                    onChangeText={setNewKeyName}
                    placeholderTextColor="#9ca3af"
                  />
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>Expires (optional)</Text>
                  {Platform.OS === 'web' ? (
                    <input
                      type="date"
                      value={newKeyExpiresAt}
                      onChange={(e) => setNewKeyExpiresAt(e.target.value)}
                      style={{
                        padding: '8px 12px',
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        borderColor: '#d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        color: '#111827',
                        backgroundColor: '#ffffff',
                        width: '100%',
                        fontFamily: 'inherit',
                        outlineStyle: 'none',
                      }}
                    />
                  ) : (
                    <TextInput
                      style={styles.formInput}
                      placeholder="YYYY-MM-DD (leave blank to never expire)"
                      value={newKeyExpiresAt}
                      onChangeText={setNewKeyExpiresAt}
                      placeholderTextColor="#9ca3af"
                    />
                  )}
                </View>

                {error && (
                  <View style={styles.errorContainer}>
                    <Text style={styles.errorText}>{error}</Text>
                  </View>
                )}

                <View style={styles.modalFooter}>
                  <Pressable style={styles.secondaryButton} onPress={closeCreateModal}>
                    <Text style={styles.secondaryButtonText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.primaryButton, creating && styles.primaryButtonDisabled]}
                    onPress={handleGenerate}
                    disabled={creating}
                  >
                    {creating ? (
                      <ActivityIndicator size="small" color="#ffffff" />
                    ) : (
                      <>
                        <Plus size={16} color="#ffffff" />
                        <Text style={styles.primaryButtonText}>Generate</Text>
                      </>
                    )}
                  </Pressable>
                </View>
              </View>
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    minHeight: 0,
    width: '100%',
  },
  card: {
    flex: 1,
    minHeight: 0,
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 16,
    ...shadows.sm,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    flexWrap: 'wrap',
    gap: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000000',
  },
  subtitle: {
    fontSize: 13,
    color: '#6b7280',
    marginBottom: 16,
    lineHeight: 18,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#2563eb',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 6,
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  primaryButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  secondaryButton: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
  },
  secondaryButtonText: {
    color: '#374151',
    fontSize: 14,
    fontWeight: '500',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#ffffff',
    marginBottom: 12,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#111827',
    paddingVertical: 4,
  },
  clearSearchButton: {
    padding: 4,
  },
  errorContainer: {
    backgroundColor: '#fee2e2',
    padding: 10,
    borderRadius: 6,
    marginBottom: 12,
  },
  errorText: {
    color: '#dc2626',
    fontSize: 13,
  },
  successContainer: {
    backgroundColor: '#d1fae5',
    padding: 10,
    borderRadius: 6,
    marginBottom: 12,
  },
  successText: {
    color: '#065f46',
    fontSize: 13,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 32,
    minHeight: 200,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 32,
    minHeight: 200,
    gap: 8,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginTop: 8,
  },
  emptyText: {
    fontSize: 13,
    color: '#6b7280',
    textAlign: 'center',
    maxWidth: 380,
  },
  listScroll: {
    flex: 1,
    minHeight: 0,
  },
  list: {
    gap: 10,
  },
  keyCard: {
    backgroundColor: '#ffffff',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 14,
    ...shadows.sm,
    gap: 6,
  },
  keyCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  keyCardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flexShrink: 1,
  },
  keyName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
  },
  iconButton: {
    padding: 6,
    borderRadius: 6,
  },
  iconButtonDisabled: {
    opacity: 0.4,
  },
  keyCardRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 8,
  },
  metaLabel: {
    fontSize: 12,
    color: '#6b7280',
    width: 80,
    fontWeight: '500',
  },
  metaValue: {
    fontSize: 13,
    color: '#111827',
    flexShrink: 1,
  },
  monoValue: {
    fontSize: 13,
    color: '#111827',
    fontFamily: Platform.select({ web: 'ui-monospace, SFMono-Regular, Menlo, monospace', default: 'monospace' }) as string,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: '600',
  },
  statusBadgeActive: {
    backgroundColor: '#dcfce7',
  },
  statusBadgeRevoked: {
    backgroundColor: '#fee2e2',
  },
  statusBadgeExpired: {
    backgroundColor: '#fef3c7',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  modalCard: {
    width: '100%',
    maxWidth: 520,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    ...shadows.xl,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  modalCloseBtn: {
    padding: 4,
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 16,
  },
  formContainer: {
    gap: 14,
  },
  formGroup: {
    gap: 6,
  },
  formLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: '#374151',
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    backgroundColor: '#ffffff',
    color: '#111827',
  },
  userPickerList: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 6,
    height: 180,
    overflow: 'hidden',
  },
  userPickerScroll: {
    flex: 1,
  },
  userPickerItem: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  userPickerItemSelected: {
    backgroundColor: '#dbeafe',
  },
  userPickerItemText: {
    fontSize: 14,
    color: '#111827',
    fontWeight: '500',
  },
  userPickerItemTextSelected: {
    color: '#1e3a8a',
  },
  userPickerItemSub: {
    fontSize: 11,
    color: '#9ca3af',
    marginTop: 2,
  },
  userPickerEmpty: {
    padding: 12,
    fontSize: 13,
    color: '#6b7280',
    textAlign: 'center',
  },
  generatedContainer: {
    gap: 12,
  },
  generatedHeading: {
    fontSize: 13,
    fontWeight: '600',
    color: '#92400e',
    backgroundColor: '#fef3c7',
    padding: 10,
    borderRadius: 6,
  },
  generatedKeyBox: {
    backgroundColor: '#0f172a',
    padding: 14,
    borderRadius: 8,
  },
  generatedKeyText: {
    color: '#f8fafc',
    fontSize: 13,
    fontFamily: Platform.select({ web: 'ui-monospace, SFMono-Regular, Menlo, monospace', default: 'monospace' }) as string,
  },
  copyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: '#111827',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 6,
  },
  copyButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  generatedMeta: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    padding: 12,
    gap: 6,
  },
});

export default ApiKeysTab;
